// AudioManager — high-level facade over Web Audio + the SFX presets.
//
//   - Owns the single AudioContext (created lazily after first user gesture
//     to satisfy browser autoplay policies).
//   - Master volume + mute, persisted to localStorage.
//   - Throttles per-SFX firing to avoid 200 simultaneous hit clicks turning
//     into a mush.
//
// The manager is created up-front (cheap), but no sound plays until
// `unlock()` is called from a real user gesture (title scene click).

import {
  sfxHit,
  sfxKill,
  sfxPickupDrop,
  sfxPickupCollect,
  sfxBreed,
  sfxBallDeath,
  sfxWaveEvent,
  sfxRunEnd,
} from './sfx';
import { MusicEngine } from './music';
import type { Phase } from '../sim/systems/wave-director';

const STORAGE_KEY = 'ball-breeder:audio';

export type SfxName =
  'hit' | 'kill' | 'pickupDrop' | 'pickupCollect' | 'breed' | 'ballDeath' | 'waveEvent' | 'runEnd';

const PLAYERS: Record<SfxName, (ctx: AudioContext, out: AudioNode) => void> = {
  hit: sfxHit,
  kill: sfxKill,
  pickupDrop: sfxPickupDrop,
  pickupCollect: sfxPickupCollect,
  breed: sfxBreed,
  ballDeath: sfxBallDeath,
  waveEvent: sfxWaveEvent,
  runEnd: sfxRunEnd,
};

/** Minimum seconds between repeats of the same SFX (anti-spam). */
const THROTTLE_SEC: Partial<Record<SfxName, number>> = {
  hit: 0.04,
  kill: 0.08,
  pickupDrop: 0.15,
};

interface AudioSettings {
  volume: number; // 0..1
  muted: boolean;
}

export class AudioManager {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private musicBus: GainNode | null = null;
  private music = new MusicEngine();
  private unlocked = false;
  private settings: AudioSettings;
  private lastPlayedAt = new Map<SfxName, number>();

  constructor(private readonly storage: Storage = globalThis.localStorage) {
    this.settings = loadSettings(storage);
  }

  /** Must be called from a real user-gesture handler. Resumes / creates the
   *  AudioContext. After this, play() works. */
  async unlock(): Promise<void> {
    if (this.unlocked) {
      // Some browsers suspend the context on tab visibility changes.
      if (this.ctx && this.ctx.state === 'suspended') await this.ctx.resume();
      return;
    }
    try {
      const Ctx =
        globalThis.AudioContext ??
        (globalThis as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctx) return;
      this.ctx = new Ctx();
      this.master = this.ctx.createGain();
      this.master.gain.value = this.effectiveVolume();
      this.master.connect(this.ctx.destination);
      // Music gets its own attenuated bus under master so SFX always
      // sit on top of it without ducking.
      this.musicBus = this.ctx.createGain();
      this.musicBus.gain.value = 0.35;
      this.musicBus.connect(this.master);
      if (this.ctx.state === 'suspended') await this.ctx.resume();
      this.unlocked = true;
    } catch {
      // Audio unavailable; silently no-op forever.
      this.ctx = null;
      this.master = null;
    }
  }

  play(name: SfxName): void {
    if (!this.unlocked || !this.ctx || !this.master) return;
    if (this.settings.muted) return;
    const now = this.ctx.currentTime;
    const throttle = THROTTLE_SEC[name];
    if (throttle !== undefined) {
      const last = this.lastPlayedAt.get(name) ?? -Infinity;
      if (now - last < throttle) return;
      this.lastPlayedAt.set(name, now);
    }
    PLAYERS[name](this.ctx, this.master);
  }

  setVolume(v: number): void {
    this.settings.volume = clamp01(v);
    this.persist();
    if (this.master) this.master.gain.value = this.effectiveVolume();
  }

  /** Start music at a given phase. Idempotent after start. */
  startMusic(phase: Phase): void {
    if (!this.unlocked || !this.ctx || !this.musicBus) return;
    this.music.start(this.ctx, this.musicBus, phase);
  }

  /** Switch the active phase with a cross-fade. */
  setMusicPhase(phase: Phase): void {
    if (!this.unlocked) return;
    this.music.setPhase(phase);
  }

  /** Stop all music with a short fade. */
  stopMusic(fadeSec: number = 1): void {
    this.music.stop(fadeSec);
  }

  toggleMute(): boolean {
    this.settings.muted = !this.settings.muted;
    this.persist();
    if (this.master) this.master.gain.value = this.effectiveVolume();
    return this.settings.muted;
  }

  isMuted(): boolean {
    return this.settings.muted;
  }

  getVolume(): number {
    return this.settings.volume;
  }

  private effectiveVolume(): number {
    return this.settings.muted ? 0 : this.settings.volume;
  }

  private persist(): void {
    try {
      this.storage.setItem(STORAGE_KEY, JSON.stringify(this.settings));
    } catch {
      // localStorage may be unavailable in private mode; ignore.
    }
  }
}

function loadSettings(storage: Storage): AudioSettings {
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return { volume: 0.6, muted: false };
    const parsed = JSON.parse(raw) as Partial<AudioSettings>;
    return {
      volume: typeof parsed.volume === 'number' ? clamp01(parsed.volume) : 0.6,
      muted: typeof parsed.muted === 'boolean' ? parsed.muted : false,
    };
  } catch {
    return { volume: 0.6, muted: false };
  }
}

function clamp01(v: number): number {
  if (v < 0) return 0;
  if (v > 1) return 1;
  return v;
}
